// Photo Filter — Photoshop-style adjustment.
//
// Simulates the effect of placing a colored filter over the camera lens.
// Blends each pixel with the filter color based on density, with optional
// luminosity preservation.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface PhotoFilterParams {
  /** RGB color of the filter (0..255 per channel). */
  color: { r: number; g: number; b: number };
  /** Strength of the filter in percent, 0..100. Default 25. */
  density: number;
  /** When true, scale the output to match the original luminosity. Default true. */
  preserveLuminosity: boolean;
}

const DEFAULT_PARAMS: PhotoFilterParams = {
  color: { r: 255, g: 180, b: 50 },
  density: 25,
  preserveLuminosity: true,
};

export const PhotoFilter: Filter<PhotoFilterParams> = {
  id: "photo-filter",
  displayName: "Photo Filter",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: PhotoFilterParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const density = clampRange(params.density, 0, 100);
    const filterFactor = density / 100;
    const preserveLum = params.preserveLuminosity;
    const { r: fr, g: fg, b: fb } = params.color;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      if (a === 0) {
        dst[o] = 0;
        dst[o + 1] = 0;
        dst[o + 2] = 0;
        dst[o + 3] = 0;
        continue;
      }

      let outR = r * (1 - filterFactor) + fr * filterFactor;
      let outG = g * (1 - filterFactor) + fg * filterFactor;
      let outB = b * (1 - filterFactor) + fb * filterFactor;

      if (preserveLum) {
        const origLum = 0.3 * r + 0.59 * g + 0.11 * b;
        const outLum = 0.3 * outR + 0.59 * outG + 0.11 * outB;
        if (outLum > 0) {
          const scale = origLum / outLum;
          outR = clamp01(outR * scale) * 255;
          outG = clamp01(outG * scale) * 255;
          outB = clamp01(outB * scale) * 255;
        } else {
          outR = 0;
          outG = 0;
          outB = 0;
        }
      }

      dst[o] = Math.round(outR);
      dst[o + 1] = Math.round(outG);
      dst[o + 2] = Math.round(outB);
      dst[o + 3] = a;
    }

    return out;
  },
};

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return 0;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
